import { memo } from 'react';
import { formatCell } from '@/lib/values';
import type { CellValue, DuckDbType } from '@/types/api';

export interface GridCellProps {
  readonly value: CellValue;
  readonly type: DuckDbType;
  readonly width: number;
  readonly rowIndex: number;
  readonly colIndex: number;
  readonly selected: boolean;
  readonly focused: boolean;
  readonly onSelect: (rowIndex: number, colIndex: number) => void;
}

/**
 * One result cell.
 *
 * Values are never interpolated raw: `formatCell` is the only path to the
 * screen, because a BIGINT reaching JSX throws, and because NULL and the empty
 * string must be visually distinct (`.v-null` vs `.v-empty`).
 */
function GridCellImpl({
  value,
  type,
  width,
  rowIndex,
  colIndex,
  selected,
  focused,
  onSelect,
}: GridCellProps): JSX.Element {
  const cell = formatCell(value, type, { groupDigits: true, maxLength: 120 });

  const className =
    cell.kind === 'null' ? 'grid-cell v-null' : cell.kind === 'empty' ? 'grid-cell v-empty' : 'grid-cell';

  return (
    <div
      role="gridcell"
      aria-rowindex={rowIndex + 2}
      aria-colindex={colIndex + 2}
      aria-selected={selected}
      tabIndex={focused ? 0 : -1}
      data-testid={`cell-${rowIndex}-${colIndex}`}
      data-cell-kind={cell.kind}
      data-selected={selected ? 'true' : 'false'}
      data-align={cell.align}
      className={className}
      style={{ width, minWidth: width, height: '100%' }}
      title={cell.kind === 'empty' ? 'Empty string' : cell.full}
      onMouseDown={() => onSelect(rowIndex, colIndex)}
    >
      {cell.text}
    </div>
  );
}

export const GridCell = memo(GridCellImpl);
